import {
  bookingSuggestionConfidenceLevels,
  type BookingSuggestionDto,
} from '../Types/expenseInvoice.types';
import type { BookingSuggestion } from './expenseInvoiceBookingSuggestions.contract';

const isValidConfidence = (value: unknown): value is BookingSuggestion['confidence'] =>
  typeof value === 'string' &&
  (bookingSuggestionConfidenceLevels as readonly string[]).includes(value);

const isValidIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const normalizeReasons = (reasons: string[] | null | undefined): string[] =>
  Array.isArray(reasons)
    ? reasons.filter((reason): reason is string => typeof reason === 'string')
    : [];

/**
 * Maps a single raw `BookingSuggestionDto` API response item to the local
 * `BookingSuggestion` read contract. Returns `null` for a malformed item instead
 * of throwing, so one bad entry does not break the whole suggestions list.
 */
export const mapBookingSuggestionDto = (
  suggestion: BookingSuggestionDto
): BookingSuggestion | null => {
  if (
    typeof suggestion?.bookingId !== 'number' ||
    !Number.isFinite(suggestion.bookingId) ||
    typeof suggestion.offerName !== 'string' ||
    !isValidIsoDate(suggestion.bookingDateFrom) ||
    !isValidIsoDate(suggestion.bookingDateTo) ||
    typeof suggestion.numberOfParticipants !== 'number' ||
    typeof suggestion.score !== 'number' ||
    !isValidConfidence(suggestion.confidence)
  ) {
    return null;
  }

  return {
    bookingId: suggestion.bookingId,
    offerName: suggestion.offerName,
    bookingDateFrom: suggestion.bookingDateFrom,
    bookingDateTo: suggestion.bookingDateTo,
    numberOfParticipants: suggestion.numberOfParticipants,
    score: suggestion.score,
    confidence: suggestion.confidence,
    reasons: normalizeReasons(suggestion.reasons),
  };
};

/**
 * Maps the raw `booking-suggestions` API response array to the local
 * `BookingSuggestion[]` read contract, dropping malformed entries.
 */
export const mapBookingSuggestionsResponse = (
  response: BookingSuggestionDto[] | null | undefined
): BookingSuggestion[] =>
  (response ?? [])
    .map((suggestion) => mapBookingSuggestionDto(suggestion))
    .filter((suggestion): suggestion is BookingSuggestion => suggestion !== null);
